import type { CartesianPose } from '@sb/geometry';
import {
  CameraRelativeToWristInTooltipCoordinates,
  getTooltipCoordinatesTransform,
} from '@sb/geometry';

import type { DeviceConfiguration } from '../types';

// The intrinsics calibration produces a wildly wrong wrist-to-camera
// transform, so the fixed constant stands in for it. The branch stays because
// the calibrated value is the intended source once it is trustworthy.
const USE_INTRINSICS_VALUE = false;

/**
 * The flange→camera transform a camera row defines, in the tooltip-axis
 * convention the rest of the tooltip stack composes in. `undefined` when the
 * row defines none, and when the kind is not a camera at all.
 *
 * The built-in wrist camera sits at a fixed place on the wrist and needs no
 * calibration to be placed. A `Camera` is placed only by extrinsics selected
 * on the row and calibrated against the tool frame: a base-frame calibration
 * describes where the camera is in the cell, not where it is on the arm, so it
 * names no tooltip.
 *
 * The one derivation for both, so the end-effector chain and each device's
 * `DeviceKinematics` cannot answer the same row differently.
 */
export function getCameraTooltipTransform(
  config: DeviceConfiguration,
): CartesianPose | undefined {
  if (config.kind === 'WristCamera') {
    return USE_INTRINSICS_VALUE &&
      config.isIntrinsicsCalibrationEnabled &&
      config.intrinsicsWristToCameraTransform
      ? config.intrinsicsWristToCameraTransform
      : CameraRelativeToWristInTooltipCoordinates;
  }

  if (config.kind !== 'Camera') return undefined;

  const entry =
    config.extrinsicsId == null
      ? undefined
      : config.extrinsicsData[config.extrinsicsId];

  if (entry?.extrinsics == null || entry.kind !== 'tcp') return undefined;

  // Extrinsics are stored in the camera's own axis convention; the tooltip
  // stack reads offsets in tooltip coordinates.
  return getTooltipCoordinatesTransform(entry.extrinsics);
}
